<template>
  <section class="card">
    <h2 class="name_section">Release info</h2>
    <div class="body">
      <div v-if="isLoading" class="loader-container">
        <div class="loader-content">
          <v-progress-circular
            indeterminate
            color="#20C4F5"
            size="64"
            width="5"
          ></v-progress-circular>
          <div class="loader-text">
            <h3>Loading release information</h3>
            <p>Please wait while we retrieve your data...</p>
          </div>
        </div>
      </div>
      <div v-else>
      <div class="details full_width">
        <div class="item_row">
          <v-text-field
            :class="{'field_error': $v.details.title.$error || releaseInfoErrorMessage === 'artist_already_have_release_with_title',
            'disabled': createdAlbum && createdAlbum.beenPublished }"
            v-model="details.title"
            label="Title"
            :readonly="createdAlbum && createdAlbum.beenPublished || false"
            required
            @input="$v.details.title.$touch()"
            @blur="$v.details.title.$touch()"
            @keydown="blockTitleInput"
          ></v-text-field>
          <span class="error-msg" v-if="$v.details.title.$error && !$v.details.title.maxLength">{{ details.title.length }}/50</span>
          <span class="error-msg" v-if="$v.details.title.$error && !$v.details.title.required">Title is required.</span>
          <span class="error-msg" v-if="releaseInfoErrorMessage === 'artist_already_have_release_with_title'">
            Release with this name already exists under the chosen artist account.
          </span>
        </div>

        <div class="item_row">
          <v-textarea
            v-model="details.description"
            :class="{'field_error': $v.details.description.$error}"
            couner
            label="Description"
            required
            auto-grow
            @input="$v.details.description.$touch()"
            @keydown="blockDescriptionTextarea"
            @blur="$v.details.description.$touch()">
          </v-textarea>
          <span class="error-msg"
                v-if="$v.details.description.$error && !$v.details.description.maxLength">{{ details.description.length }}/2000</span>
        </div>

        <!-- TAGS - commented out in original -->
        <!-- <div class="wrap_tags">...</div> -->

        <div class="row_fields">
          <!-- GENRE -->
          <div class="dropdown_style dropdown_disable" :class="{'disabled': !genres.length}">
            <v-menu offset-y content-class="navy_blue" v-model="genresDropdown" :close-on-click="true" disabled
                    :close-on-content-click="true">
              <template v-slot:activator="{ on, attrs }">
                <div class="dropdown_inside" v-bind="attrs" v-on="on">
                  <div>{{ details.genre.name }}</div>
                  <label :class="{'active': details.genre.id}">Genre</label>
                </div>
              </template>

              <!-- Genre list - commented out in original -->
            </v-menu>
          </div>

          <!-- SUBGENRE -->
          <div class="dropdown_style" :class="{'disabled': !subGenresList.length}">
            <v-menu offset-y content-class="navy_blue" v-model="subGenresDropDown" :close-on-click="true"
                    :close-on-content-click="true">
              <template v-slot:activator="{ on, attrs }">
                <div class="dropdown_inside" v-bind="attrs" v-on="on">
                  <input type="text" required v-model="details.subgenre.name"
                        :class="{'field_error': $v.details.subgenre.id.$error}"
                        @focus="subGenresDropDown = true" @keydown="keyHandler">
                  <label :class="{'active': details.subgenre.id}">Sub Genre</label>
                </div>
              </template>

              <v-list v-if="subGenresList.length">
                <v-list-item v-for="item in subGenresList" :key="item.id" @click="setSubgenre(item)"
                              :class="{'active': item.id === details.subgenre.id}">
                    <button type="button" v-ripple>{{ item.name }}</button>
                </v-list-item>
              </v-list>
            </v-menu>
            <span class="error-msg" v-if="$v.details.subgenre.id.$error">Sub Genre is required.</span>
          </div>

          <!-- RELEASE DATE -->
          <div class="dropdown_style on_change">
            <v-menu
              v-model="releaseDateDatePicker"
              :close-on-content-click="false"
              ref="releaseDateDatePicker"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto">
              <template v-slot:activator="{ on, attrs }" v-on="on">
                <div class="dropdown_inside" v-bind="attrs" v-on="on">
                  <input type="text" v-model="dateFormatted" :class="{'field_error': $v.datePicker.$error}"
                        @focus="releaseDateDatePicker = true">
                  <label :class="{'active': datePicker}">Release Date</label>
                </div>
              </template>
              <v-date-picker
                v-model="datePicker"
                no-title
                width="320"
                show-adjacent-months
                ok-text="OK"
                cancel-text="Cancel"
                :weekday-format="getDay">
              </v-date-picker>
            </v-menu>
            <span class="error-msg" v-if="$v.datePicker.$error">Release Date is required.</span>
          </div>
        </div>

        <!-- ACCOUNTS -->
        <!-- Account Selection Section with Disabled Fields - Full Width -->
        <div class="item_row">
          <div class="account-selection-section full_width">
            <div class="section-header">
              <h3>Account & Artist Selection</h3>
              <div class="selection-guidance">
                <span>Use the Account Selection Wizard to configure these settings</span>
                <div class="wizard-arrow"></div>
              </div>
            </div>
            
            <div class="row_fields single-line-fields">
              <!-- LABEL - Always visible but disabled -->
              <div ref="labelInputDiv" class="dropdown_style dropdown_disable" 
                :class="{
                  'disabled': !details.label.id,
                  'input-filled': wizardHasBeenOpened && getLineState('labelLine') === 'filled',
                  'input-active': wizardHasBeenOpened && getLineState('labelLine') === 'active',
                  'input-dimmed': wizardHasBeenOpened && getLineState('labelLine') === 'dimmed'
                }">
                <v-menu offset-y content-class="navy_blue" disabled :close-on-content-click="true">
                  <template v-slot:activator="{ on, attrs }">
                    <div class="dropdown_inside" v-bind="attrs" v-on="on">
                      <div>{{ details.label.title || 'Not Selected' }}</div>
                      <label :class="{'active': details.label.id}">Record Label</label>
                    </div>
                  </template>
                </v-menu>
                <span class="error-msg" v-if="labelError">Record Label is required for Various Artists.</span>
              </div>

              <!-- Structure Selection - Always visible but disabled -->
              <div ref="structureInputDiv" class="dropdown_style dropdown_disable hidden" :class="{'disabled': !releaseStructure}">
                <v-menu offset-y content-class="navy_blue" disabled :close-on-content-click="true">
                  <template v-slot:activator="{ on, attrs }">
                    <div class="dropdown_inside" v-bind="attrs" v-on="on">
                      <div>{{ releaseStructure || 'Not Selected' }}</div>
                      <label :class="{'active': releaseStructure}">Structure</label>
                    </div>
                  </template>
                </v-menu>
                <span class="error-msg" v-if="$v.releaseStructure.$error">Structure is required.</span>
              </div>

              <!-- ARTIST - Always visible but conditionally labeled -->
              <div ref="artistInputDiv" class="dropdown_style dropdown_disable" 
                :class="{
                  'disabled': !details.artist.id, 
                  'input-filled': wizardHasBeenOpened && getLineState('artistLine') === 'filled',
                  'input-active': wizardHasBeenOpened && getLineState('artistLine') === 'active',
                  'input-dimmed': wizardHasBeenOpened && getLineState('artistLine') === 'dimmed'
                }">
                <v-menu offset-y content-class="navy_blue" disabled :close-on-content-click="true">
                  <template v-slot:activator="{ on, attrs }">
                    <div class="dropdown_inside" v-bind="attrs" v-on="on">
                      <div>{{ details.artist.name || 'Not Selected' }}</div>
                      <label :class="{'active': details.artist.id}">
                        {{ releaseStructure === 'Multi-Artist Release' ? 'Main Artist' : 'Artist' }}
                      </label>
                    </div>
                  </template>
                </v-menu>
                <span class="error-msg" v-if="$v.details.artist.name.$error">{{ releaseStructure === 'Multi-Artist Release' ? 'Main Artist' : 'Artist' }} is required.</span>
                <span class="error-msg" v-if="artistSubscriptionError">Error artist subscription</span>
              </div>

              <!-- SECONDARY ARTISTS - Always visible but conditionally shown/hidden -->
              <div ref="secondaryArtistsInputDiv" class="dropdown_style dropdown_disable" 
                :class="{
                  'disabled': !details.secondaryArtists.length,
                  'input-filled': wizardHasBeenOpened && getLineState('secondaryArtistsLine') === 'filled',
                  'input-active': wizardHasBeenOpened && getLineState('secondaryArtistsLine') === 'active',
                  'input-dimmed': wizardHasBeenOpened && getLineState('secondaryArtistsLine') === 'dimmed'
                }">
                <v-menu offset-y content-class="navy_blue" disabled :close-on-content-click="true">
                  <template v-slot:activator="{ on, attrs }">
                    <div class="dropdown_inside" v-bind="attrs" v-on="on">
                      <div>{{ secondaryArtistsDisplay || 'Not Selected' }}</div>
                      <label :class="{'active': details.secondaryArtists.length > 0}">Secondary Artists</label>
                    </div>
                  </template>
                </v-menu>
              </div>
            </div>
          
          <!-- Account Selection Wizard Button - More Prominent -->
          <div class="wizard-button-container">
            <button 
              ref="wizardButton"
              type="button" 
              class="account-wizard-btn" 
              :class="{'completed': allRelevantLinesFilled}"
              @click="openAccountWizard" 
              v-ripple
            >
              Account Selection Wizard
            </button>
          </div>
          <svg ref="linesSvg" class="guidance-lines-svg">
            <!-- DIMMED LINES (Gray) - Always at the bottom -->
            <g 
              v-for="line in dimmedLines" 
              :key="line.id + '-dimmed'"
            >
              <path
                :d="line.d"
                :class="['animated-guidance-line', 'dimmed']" 
              />
              <circle 
                v-if="line.d"
                :class="['energy-pip', 'dimmed']" 
                r="1.5">
                <animateMotion 
                  :path="line.d" 
                  dur="0s" 
                  repeatCount="indefinite" 
                  rotate="auto" 
                />
              </circle>
            </g>

            <!-- FILLED LINES (Green) - Middle layer -->
            <g 
              v-for="line in filledLines" 
              :key="line.id + '-filled'"
            >
              <path
                :d="line.d"
                :class="['animated-guidance-line', 'filled']" 
              />
              <circle 
                v-if="line.d"
                :class="['energy-pip', 'filled']" 
                r="1.5">
                <animateMotion 
                  :path="line.d" 
                  dur="0s" 
                  repeatCount="indefinite" 
                  rotate="auto" 
                />
              </circle>
            </g>

            <!-- ACTIVE LINES (Blue) - Always on top -->
            <g 
              v-for="line in activeLines" 
              :key="line.id + '-active'"
            >
              <path
                :d="line.d"
                class="animated-guidance-line" 
              />
              <circle 
                v-if="line.d"
                class="energy-pip" 
                r="1.5">
                <animateMotion 
                  :path="line.d" 
                  dur="2s" 
                  repeatCount="indefinite" 
                  rotate="auto" 
                />
              </circle>
            </g>
          </svg>
        </div>
      </div>
      </div>

      <div class="message_error release_info" v-if="releaseInfoErrorMessage !== 'artist_already_have_release_with_title'">
        {{ releaseInfoErrorMessage }}
      </div>
      
      <!-- Account Selection Wizard Dialog -->
      <!-- NEW: Added listener for wizard pause state to track sub-dialog openings -->
      <account-selection-wizard
        v-model="showAccountWizard"
        @account-type-selected="handleAccountTypeSelected"
        @structure-selected="handleStructureSelected"
        @structure-preview-changed="handleStructurePreviewChanged" 
        @flow-type-preview-changed="handleFlowTypePreviewChanged"
        @wizard-pause-state-changed="handleWizardPauseStateChanged"
        @record-label-selection-cancelled="handleRecordLabelSelectionCancelled"
        @secondary-artists-selection-cancelled="handleSecondaryArtistsSelectionCancelled"
      />
      
      <!-- Track Artist Selector Dialog - Moved outside row_fields to prevent layout issues -->
      <track-artist-selector
        v-if="releaseStructure === 'Multi-Artist Release'"
        v-model="showSecondaryArtistsDialog"
        :selected-artists="details.secondaryArtists"
        :album-artists="details.artist.id ? [details.artist] : []"
        @selection-confirmed="onSecondaryArtistsSelected"
      />
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { maxLength, required } from "vuelidate/lib/validators";
import { blockTitleInput, blockDescriptionTextarea, numbersOnly, keyHandler } from "@/utils/formValidators";
import { formatDate, convertToDatePicker, getDay } from "@/utils/dateFormatters";
import TrackArtistSelector from "@/components/TrackArtistSelector.vue";
import AccountSelectionWizard from "@/components/musicRelease/AccountSelectionWizard.vue";

export default {
  name: "ReleaseInfo",
  components: {
    TrackArtistSelector,
    AccountSelectionWizard
  },
  props: {
    id: {
      type: String,
      default: null
    },
    editRelease: {
      type: Boolean,
      default: false
    },
    createLoading: {
      type: Boolean,
      default: false
    },
    releaseInfoChanged: {
      type: Boolean,
      default: false
    },
    savedStructure: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      // Release info specific data
      details: {
        title: '',
        description: '',
        tags: [],
        genre: { id: 2, name: 'Psychedelic Trance' },
        subgenre: { id: '', name: '' },
        artist: { id: '', name: '' },
        secondaryArtists: [],
        label: { id: '', title: '' }
      },
      secondaryArtistsDisplay: '',
      artistSubscriptionError: false,
      showSecondaryArtistsDialog: false,

      // Dropdowns
      genresDropdown: false,
      subGenresDropDown: false,
      artistDropDown: false,

      // Lists
      genresList: '',
      allSubGenresList: '',
      subGenresList: '',
      artistsList: '',
      recordLabelsList: '',

      // Tags
      newTag: '',

      // Release Date Picker
      datePicker: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
      dateFormatted: '',
      releaseDateDatePicker: false,

      // Account selection
      viewListAccounts: null, // label or artist

      // Add a simple flag for account error
      accountError: false,
      
      // Track if label was explicitly cleared by user
      labelExplicitlyCleared: false,
      labelError: false,

      releaseStructure: '',
      
      // Loading state for data restoration
      isLoading: false,
      
      // Account selection wizard
      showAccountWizard: false,
      wizardHasBeenOpened: false, // Track if the wizard has been opened at least once
      guidanceLines: [], // Will store { id, refName, visible, d: '', isFilled: false, isDimmed: false }
      previewedStructure: null, // For live preview from wizard
      previewedFlowType: null, // For live preview of flow type (artistFlow/labelFlow)
      lastFlowType: null, // Track the last flow type after it's been cleared
      isWizardSubDialogActive: false, // NEW: Tracks if a wizard sub-dialog (selector) is active
    }
  },
  validations: {
    details: {
      title: {
        required,
        maxLength: maxLength(50)
      },
      description: {
        maxLength: maxLength(2000)
      },
      tags: {
        required
      },
      genre: {
        id: {
          required,
        }
      },
      subgenre: {
        id: {
          required,
        }
      },
      artist: {
        name: {
          required
        }
      }
    },
    datePicker: {
      required
    },
    releaseStructure: { required }
  },
  computed: {
    ...mapGetters([
      'CREATED_ALBUM',
      'GENRES',
      'SUBGENRES',
      'YOUR_ARTISTS',
      'YOUR_RECORD_LABELS',
      'RELEASE_INFO_ERROR_MESSAGE',
      'PRIMARY_ARTIST',
      'SELECTED_LABEL',
      'VARIOUS_ARTISTS',
      'RELEASE_STRUCTURE',
      'TEMP_CREATED_ALBUMS_ID',
      'userID',
      'secondaryArtists'
    ]),
    createdAlbum() {
      return this.CREATED_ALBUM;
    },
    genres() {
      return this.GENRES;
    },
    releaseInfoErrorMessage() {
      return this.RELEASE_INFO_ERROR_MESSAGE;
    },
    variousArtists() {
      return this.VARIOUS_ARTISTS;
    },
    secondaryArtistsFromStore() {
      console.log('ReleaseInfo - Accessing secondaryArtistsFromStore computed property');
      console.log('ReleaseInfo - Value from Vuex:', this.secondaryArtists);
      return this.secondaryArtists;
    },
    allRelevantLinesFilled() {
      // Only check visible and not dimmed lines (relevant lines)
      const relevantLines = this.guidanceLines.filter(line => line.visible && !line.isDimmed);
      // If no relevant lines, return false
      if (relevantLines.length === 0) return false;
      // Check if all relevant lines are filled
      return relevantLines.every(line => line.isFilled);
    },
    // NEW: Computed properties for line rendering order
    visibleGuidanceLines() {
      return this.guidanceLines.filter(l => l.visible);
    },
    dimmedLines() {
      // Lines that should appear gray
      // This considers the wizardHasBeenOpened flag for initial gray state
      return this.visibleGuidanceLines.filter(l => !this.wizardHasBeenOpened || l.isDimmed);
    },
    filledLines() {
      // Lines that should appear green
      // Only if wizard has been opened and line is not dimmed but is filled
      return this.visibleGuidanceLines.filter(l => this.wizardHasBeenOpened && !l.isDimmed && l.isFilled);
    },
    activeLines() {
      // Lines that should appear blue
      // Only if wizard has been opened and line is not dimmed and not filled
      return this.visibleGuidanceLines.filter(l => this.wizardHasBeenOpened && !l.isDimmed && !l.isFilled);
    }
  },
  watch: {
    isLoading(newVal, oldVal) {
      if (oldVal === true && newVal === false) { // Just finished loading
        this.$nextTick(() => {
          requestAnimationFrame(this.updateGuidanceLines);
        });
      }
    },
    // Watch for changes in the Vuex secondaryArtists state using computed property
    secondaryArtistsFromStore: {
      handler(newVal) {
        console.log('ReleaseInfo - secondaryArtistsFromStore watcher triggered:', newVal);
        if (newVal && Array.isArray(newVal)) {
          // Force update the secondary artists display
          this.updateSecondaryArtistsDisplay(newVal);
        }
      },
      immediate: true, // Run immediately when component is created
      deep: true // Watch deeply for changes in the array
    },
    'details.title': {
      handler(oldVal, newVal) {
        if (oldVal !== newVal) {
          if (oldVal && newVal) {
            if (oldVal.length > 51) {
              return
            }
            this.$emit('update:releaseInfoChanged', true);
            this.$store.commit('SET_RELEASE_INFO_ERROR_MESSAGE', '');
          }
        }
      },
      deep: true
    },
    'details.description': {
      handler(oldVal, newVal) {
        if (oldVal !== newVal) {
          if (oldVal && newVal) {
            this.$emit('update:releaseInfoChanged', true);
            this.$store.commit('SET_RELEASE_INFO_ERROR_MESSAGE', '');
          }
        }
      },
      deep: true
    },
    'details.subgenre.id': {
      handler(newVal, oldVal) {
        if (this.createdAlbum && this.createdAlbum.subGenres && !this.createdAlbum.subGenres.length) {
          this.$emit('update:releaseInfoChanged', true);
        }

        if (oldVal !== this.details.subgenre.id) {
          if (oldVal) {
            this.$emit('update:releaseInfoChanged', true);
            this.$store.commit('SET_RELEASE_INFO_ERROR_MESSAGE', '');
          }
        }
      },
      deep: true
    },
    datePicker() {
      this.dateFormatted = this.formatDate(this.datePicker);
      this.$emit('update:releaseInfoChanged', true);
      this.releaseDateDatePicker = false;
      this.$store.commit('SET_RELEASE_INFO_ERROR_MESSAGE', '');
    },
    PRIMARY_ARTIST(newValue) {
      if (newValue) {
        if (this.viewListAccounts === 'secondaryArtists') {
          // Add to secondary artists array if not already there
          const artistExists = this.details.secondaryArtists.some(artist => artist.id === newValue.id);
          if (!artistExists) {
            this.details.secondaryArtists.push({
              id: newValue.id,
              name: newValue.name
            });
            // Update display text
            this.secondaryArtistsDisplay = this.details.secondaryArtists.map(artist => artist.name).join(', ');
            this.$emit('update:releaseInfoChanged', true);
          }
        } else {
          // Default case - artist/main artist
          this.details.artist.id = newValue.id;
          this.details.artist.name = newValue.name;
          this.artistSubscriptionError = false;
          this.$emit('update:artist-id', this.details.artist.id);
        }
      } else {
        // Handle clearing the artist
        if (this.viewListAccounts !== 'secondaryArtists' && this.releaseStructure === 'Multi-Artist Release') {
          // Clear the main artist field when primary artist is cleared
          this.details.artist.id = '';
          this.details.artist.name = '';
          this.$emit('update:artist-id', null);
          this.$emit('update:releaseInfoChanged', true);
        }
      }
    },
    RELEASE_STRUCTURE(newValue) {
      console.log('RELEASE_STRUCTURE watcher triggered with:', newValue);
      // Update local structure state when Vuex store changes
      if (newValue !== this.releaseStructure) {
        console.log('Updating local releaseStructure from Vuex store:', newValue);
        this.releaseStructure = newValue;
      }
      // Update guidance lines when structure changes, affecting input visibility
      this.$nextTick(() => {
        if (this.updateGuidanceLines && !this.isLoading) { // Check isLoading
          this.updateGuidanceLines();
        }
      });
    },
    
    SELECTED_LABEL(newValue) {
      console.log('SELECTED_LABEL watcher triggered with:', newValue);
      if (newValue) {
        // Label has been selected
        this.details.label.id = newValue.id;
        this.details.label.title = newValue.title;
        
        // IMPORTANT: We DO NOT clear the artist selection anymore
        // This allows both artist and label to be set simultaneously
        
        // Reset explicit clearing flag when a label is selected
        this.labelExplicitlyCleared = false;
        
        // Reset label error when a label is selected
        this.labelError = false;
        
        // Mark form as changed whenever label is selected
        this.$emit('update:releaseInfoChanged', true);
        console.log('Form marked as changed due to label selection');
      } else if (newValue === null) {
        // Handle explicit null selection (clearing)
        this.details.label.id = '';
        this.details.label.title = '';
        
        // Check if we need to set label error for Various Artists Release
        if (this.releaseStructure === 'Various Artists Release') {
          this.labelError = true;
        }
        
        this.$emit('update:releaseInfoChanged', true);
      }
    },
    id: {
      handler() {
        if (this.id) {
          this.updateComponentData();
        }
      },
      immediate: true
    },
    variousArtists(newValue) {
      if (newValue === true) {
        // When switching to Various Artists mode, clear the artist selection
        this.details.artist.id = '';
        this.details.artist.name = '';
        
        // Notify parent component
        this.$emit('update:artist-id', null);
        this.$emit('update:releaseInfoChanged', true);
        
        console.log('Cleared artist in ReleaseInfo due to Various Artists mode');
      }
    },
    'details.label.id': function(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$nextTick(this.updateGuidanceLines);
      }
    },
    'details.artist.id': function(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$nextTick(this.updateGuidanceLines);
      }
    },
    'details.secondaryArtists': {
      handler: function(newVal, oldVal) {
        // Basic check to avoid unnecessary updates if the array reference is the same
        // and length hasn't changed (deep watcher handles content changes)
        if (newVal !== oldVal || (newVal && oldVal && newVal.length !== oldVal.length)) {
          this.$nextTick(this.updateGuidanceLines);
        }
      },
      deep: true
    },
    showAccountWizard(newVal, oldVal) {
      if (!newVal) { // Wizard is now closed or was never opened
        this.isWizardSubDialogActive = false; // Reset sub-dialog state
        if (oldVal === true && (this.previewedStructure !== null || this.previewedFlowType !== null)) {
          // Wizard was open and is now closed, and there was an active preview
          this.previewedStructure = null;
          this.previewedFlowType = null;
          this.$nextTick(this.updateGuidanceLines);
        }
      } 
    },
    previewedFlowType(newValue, oldValue) {
      // If we had a flow type and it's being cleared, save the old value
      if (oldValue && !newValue) {
        this.lastFlowType = oldValue;
      } else if (newValue) {
        // If we get a new flow type, track it
        this.lastFlowType = newValue;
      }
    },
    'guidanceLines': {
      deep: true,
      handler() {
        // Force update to ensure input styling reflects current line states
        this.$forceUpdate();
      }
    },
  },
  created() {
    this.dateFormatted = this.formatDate(this.datePicker);

    // Emit initial artist ID
    this.$emit('update:artist-id', this.details.artist.id);

    // Initialize secondary artists from Vuex store if available
    console.log('ReleaseInfo created - Checking Vuex secondaryArtistsFromStore:', this.secondaryArtistsFromStore);
    if (this.secondaryArtistsFromStore && this.secondaryArtistsFromStore.length > 0) {
      console.log('ReleaseInfo created - Found secondaryArtistsFromStore in Vuex, initializing...');
      // Use our common method to update the display
      this.$nextTick(() => {
        this.updateSecondaryArtistsDisplay(this.secondaryArtistsFromStore);
      });
    }
    
    // Initialize structure from Vuex store if available
    if (this.RELEASE_STRUCTURE) {
      console.log('ReleaseInfo created - Found structure in Vuex store:', this.RELEASE_STRUCTURE);
      this.releaseStructure = this.RELEASE_STRUCTURE;
    }

    // Fetch initial data
    this.getGenres();

    this.GET_YOUR_ARTISTS({owner: this.userID, deletedAt: false, 'order[createdAt]': 'desc'})
      .then(() => {
        this.artistsList = this.YOUR_ARTISTS;
      })
      .catch(err => console.error(`GET_YOUR_ARTISTS, ${err}`));

    this.GET_YOUR_RECORD_LABELS({
      owner: this.userID,
      sort: 'createdAt',
      order: 'desc'
    })
      .then(() => {
        this.recordLabelsList = this.YOUR_RECORD_LABELS;
      })
      .catch(err => console.error(`GET_YOUR_RECORD_LABELS, ${err}`));

    if (this.id) {
      this.updateComponentData();
    }

    // Emit initialized event
    this.$emit('initialized', {
      details: this.details,
      datePicker: this.datePicker
    });

    // For guidance lines
    this.initGuidanceLines();
    window.addEventListener('resize', this.updateGuidanceLines);
    if (!this.isLoading) {
      this.$nextTick(() => {
        requestAnimationFrame(this.updateGuidanceLines);
      });
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateGuidanceLines);
  },
  methods: {
    handleWizardPauseStateChanged(isPaused) {
      // Store previous state to detect transitions
      const wasPaused = this.isWizardSubDialogActive;
      
      // Update the pause state
      this.isWizardSubDialogActive = isPaused;
      
      // If we're transitioning from paused to unpaused (dialog closing),
      // force update the guidance lines to reflect the current state
      if (wasPaused && !isPaused) {
        console.log('Wizard sub-dialog closed, updating guidance lines');
        this.$nextTick(() => {
          requestAnimationFrame(this.updateGuidanceLines);
        });
      }
    },
    
    handleRecordLabelSelectionCancelled() {
      console.log('Record Label selection was cancelled, updating guidance lines');
      // Force update the guidance lines to reflect the current state
      this.$nextTick(() => {
        requestAnimationFrame(this.updateGuidanceLines);
      });
    },
    
    handleSecondaryArtistsSelectionCancelled() {
      console.log('Secondary Artists selection was cancelled, updating guidance lines');
      // Force update the guidance lines to reflect the current state
      this.$nextTick(() => {
        requestAnimationFrame(this.updateGuidanceLines);
      });
    },
    
    // Account Selection Wizard Methods
    openAccountWizard() {
      // Scroll to the top of the page
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      
      // Once scrolling is initiated, open the wizard
      this.showAccountWizard = true;
      this.wizardHasBeenOpened = true; // Set flag to true when wizard is opened
    },
    
    // Get the state of a guidance line (filled, active, dimmed)
    getLineState(lineId) {
      const line = this.guidanceLines.find(l => l.id === lineId);
      if (!line || !line.visible) return 'hidden';
      if (line.isFilled) return 'filled';
      if (line.isDimmed) return 'dimmed';
      return 'active';
    },
    
    handleAccountTypeSelected(accountType) {
      console.log('Account type selected:', accountType);
      this.showAccountWizard = false;
      
      // Handle the selected account type
      if (typeof accountType === 'object' && accountType.accountType === 'Artist Account') {
        // For artist account selection
        // The artist will be set via Vuex by the wizard
      }
      this.$nextTick(this.updateGuidanceLines); // Recalculate lines
    },
    
    // Handle structure selection from the wizard
    handleStructureSelected(structure) {
      console.log('Structure selected in wizard:', structure);
      // Update the structure input field
      this.releaseStructure = structure;
      this.previewedStructure = null; // Clear preview on confirmation
      this.previewedFlowType = null; // Also clear flow type preview
      
      // Store the structure in the Vuex store for consistency across components
      this.$store.commit('SET_RELEASE_STRUCTURE', structure);
      this.$nextTick(this.updateGuidanceLines); // Recalculate lines
    },
    
    handleFlowTypePreviewChanged(flowType) {
      console.log('Flow type preview changed:', flowType);
      
      // Track if we're transitioning from Record Label to Artist Account flow
      const wasLabelFlow = this.previewedFlowType === 'labelFlow';
      const switchingToArtistFlow = flowType === 'artistFlow';
      const hasVariousArtistsStructure = this.previewedStructure === 'Various Artists Release' || this.releaseStructure === 'Various Artists Release';
      
      // If flowType is null, it means the wizard was cancelled in step 1
      // We should restore the previous state (if any)
      if (flowType === null) {
        console.log('Wizard cancelled, restoring previous flow state');
        this.previewedFlowType = null;
        this.previewedStructure = null;
        
        // Use nextTick and requestAnimationFrame to ensure DOM is updated
        this.$nextTick(() => {
          requestAnimationFrame(this.updateGuidanceLines);
        });
        return;
      }
      
      // Special handling for switching from Label flow to Artist flow with Various Artists
      // In this case, we should reset both the label and structure selections
      if (wasLabelFlow && switchingToArtistFlow && hasVariousArtistsStructure) {
        console.log('Switching from Label flow with Various Artists to Artist flow, resetting structure');
        
        // Reset both flow and structure previews
        this.previewedFlowType = flowType;
        this.previewedStructure = null; // Clear structure preview since Various Artists is only valid for Label flow
        
        // Force a more complete reset of the UI state with a slight delay
        setTimeout(() => {
          this.$nextTick(() => {
            requestAnimationFrame(this.updateGuidanceLines);
          });
        }, 20);
        return;
      }
      
      // Normal flow type preview handling
      this.previewedFlowType = flowType;
      
      // When flow type changes, specific structure preview might become invalid
      this.$nextTick(() => {
        requestAnimationFrame(this.updateGuidanceLines);
      });
    },
    
    handleStructurePreviewChanged(previewStructure) {
      console.log('Structure preview changed:', previewStructure);
      
      // Track if we're transitioning from Various Artists Release
      const wasVariousArtists = this.previewedStructure === 'Various Artists Release';
      
      // If previewStructure is null, it means the wizard was cancelled
      // or we're transitioning between structures
      if (previewStructure === null) {
        console.log('Structure preview reset to null, restoring previous structure state');
        this.previewedStructure = null;
        
        // Use nextTick and requestAnimationFrame to ensure DOM is updated
        this.$nextTick(() => {
          requestAnimationFrame(this.updateGuidanceLines);
        });
        return;
      }
      
      // Special handling for transitions from Various Artists Release
      if (wasVariousArtists && previewStructure !== 'Various Artists Release') {
        console.log('Transitioning from Various Artists Release to', previewStructure);
        // Force a more complete line state update for this transition
        setTimeout(() => {
          this.previewedStructure = previewStructure;
          this.$nextTick(() => {
            requestAnimationFrame(this.updateGuidanceLines);
          });
        }, 20); // Slight delay to ensure all states reset properly
        return;
      }
      
      // Normal structure preview handling
      this.previewedStructure = previewStructure;
      
      this.$nextTick(() => {
        requestAnimationFrame(this.updateGuidanceLines);
      });
    },
    
    ...mapActions([
      'GET_CREATED_ALBUM',
      'GET_GENRES',
      'GET_YOUR_ARTISTS',
      'GET_YOUR_RECORD_LABELS',
      'ADD_MUSIC_RELEASE',
      'EDIT_MUSIC_RELEASE'
    ]),
    
    ...mapMutations([
      'SET_SECONDARY_ARTISTS'
    ]),
    // Form validators
    blockTitleInput,
    blockDescriptionTextarea,
    numbersOnly,
    keyHandler,

    // Date formatters
    formatDate,
    convertToDatePicker,
    getDay,

    updateComponentData() {
      // Set loading state to true before fetching data
      this.isLoading = true;
      
      this.GET_CREATED_ALBUM(this.id)
        .then(() => {
          this.details.title = this.CREATED_ALBUM.title;
          this.details.description = !this.CREATED_ALBUM.description ? '' : this.CREATED_ALBUM.description;
          this.details.tags = !this.CREATED_ALBUM.tags ? [] : this.CREATED_ALBUM.tags;
          
          // Check if we have a saved structure directly from the parent component via props
          console.log('savedStructure prop value:', this.savedStructure);
          
          if (this.savedStructure) {
            // If the parent has passed a saved structure prop, use that (preserves user selection)
            this.releaseStructure = this.savedStructure;
            console.log('Using saved structure from props:', this.releaseStructure);
            
            // Make sure variousArtists flag is set correctly based on the saved structure
            if (this.savedStructure === 'Various Artists Release') {
              this.$store.commit('SET_VARIOUS_ARTISTS', true);
            }
          } else {
            // Otherwise, determine structure from API data
            // First check and set variousArtists flag
            if (this.CREATED_ALBUM.variousArtists === 1 || this.CREATED_ALBUM.variousArtists === true) {
              console.log('Setting variousArtists flag to true from album data');
              this.$store.commit('SET_VARIOUS_ARTISTS', true);
              this.releaseStructure = 'Various Artists Release';
              console.log('Setting release structure to Various Artists Release based on API data');
            } else if (this.CREATED_ALBUM.secondaryArtists && this.CREATED_ALBUM.secondaryArtists.length > 0) {
              // If there are secondary artists, it's likely a Multi-Artist Release
              this.releaseStructure = 'Multi-Artist Release';
              console.log('Setting release structure to Multi-Artist Release based on secondary artists');
            } else if (this.CREATED_ALBUM.artist) {
              // If there's just a primary artist, it's a Single Artist Release
              this.releaseStructure = 'Single Artist Release';
              console.log('Setting release structure to Single Artist Release based on primary artist');
            } else {
              // If no structure indicators found, don't set a structure
              this.releaseStructure = null;
              console.log('No structure indicators found in API data, leaving structure as null');
            }
          }

          if (this.CREATED_ALBUM.artist) {
            this.details.artist.id = this.CREATED_ALBUM.artist.id;
            this.details.artist.name = this.CREATED_ALBUM.artist.name;
            this.$emit('update:artist-id', this.details.artist.id);
          }

          // Add handling for record label
          if (this.CREATED_ALBUM.recordLabel) {
            // Check if label was explicitly cleared by user (through the UI)
            if (!this.labelExplicitlyCleared) {
              // Restore label from API if not explicitly cleared
              this.details.label.id = this.CREATED_ALBUM.recordLabel.id;
              this.details.label.title = this.CREATED_ALBUM.recordLabel.title;
              console.log('Found record label in API response, restoring:', this.details.label);
              
              // Update the store
              this.$store.commit('SET_SELECTED_LABEL', {
                id: this.CREATED_ALBUM.recordLabel.id,
                title: this.CREATED_ALBUM.recordLabel.title
              });
            } else {
              console.log('Label was explicitly cleared by user - not restoring from API');
            }
          } else if (this.SELECTED_LABEL) {
            // If no record label in API response but we have one in the store, use that
            this.details.label.id = this.SELECTED_LABEL.id;
            this.details.label.title = this.SELECTED_LABEL.title;
            console.log('Using record label from store:', this.details.label);
          }

          if (this.CREATED_ALBUM.genre) {
            this.details.genre.id = this.CREATED_ALBUM.genre.id;
            this.details.genre.name = this.CREATED_ALBUM.genre.name;

            if (!this.genresList.length) {
              this.getGenres();
            }
          }

          if (this.CREATED_ALBUM.subGenres && this.CREATED_ALBUM.subGenres.length) {
            this.details.subgenre.id = this.CREATED_ALBUM.subGenres[0].id;
            this.details.subgenre.name = this.CREATED_ALBUM.subGenres[0].name;
          }
          
          // Handle secondary artists for Multi-Artist Release
          if (this.releaseStructure === 'Multi-Artist Release' && this.CREATED_ALBUM.secondaryArtists && this.CREATED_ALBUM.secondaryArtists.length > 0) {
            // Map the secondary artists to the expected format
            this.details.secondaryArtists = this.CREATED_ALBUM.secondaryArtists.map(artist => ({
              id: artist.id,
              name: artist.name,
              avatar: artist.avatar || null
            }));
            
            // Update the display text for secondary artists
            this.secondaryArtistsDisplay = this.details.secondaryArtists.map(artist => artist.name).join(', ');
            console.log('Restored secondary artists:', this.details.secondaryArtists);
          } else {
            // Reset secondary artists if not a Multi-Artist Release
            this.details.secondaryArtists = [];
            this.secondaryArtistsDisplay = '';
          }
        })
        .catch(err => {
          console.error(`none release id= ${this.id}, error: ${err}`);
          this.$emit('navigate-to-create'); // Signal parent to handle navigation
        })
        .finally(() => {
          // Set loading state to false after data is loaded or on error
          this.isLoading = false;
        });
    },

    getGenres() {
      this.GET_GENRES()
        .then(() => {
          this.genresList = this.GENRES;
        })
        .catch(err => console.error(`getGenres, ${err}`))
        .finally(() => {
          if (this.genresList.length && this.details.genre.id) {
            let subgenreList = this.genresList.find(el => el.id === this.details.genre.id);
            this.subGenresList = subgenreList.subGenres;
          }
        });
    },

    // Genre and subgenre
    setGenre(genre) {
      this.details.genre.id = genre.id;
      this.details.genre.name = genre.name;
      this.details.subgenre.id = '';
      this.details.subgenre.name = '';
    },

    setSubgenre(subgenre) {
      this.details.subgenre.id = subgenre.id;
      this.details.subgenre.name = subgenre.name;
    },

    // Tags (commented out in template but keeping methods for completeness)
    addTag(evt) {
      evt = (evt) ? evt : window.event;
      let charCode = (evt.which) ? evt.which : evt.keyCode;

      this.newTag.trim().normalize('NFC');
      // Enter || Space || Comma || Tab
      if (this.newTag.length && (charCode === 13 || charCode === 32 || charCode === 44 || charCode === 9)) {
        evt.preventDefault();
        this.details.tags.push(this.newTag);
        this.$emit('update:releaseInfoChanged', true);
        this.newTag = '';
      }
    },

    removeTag(ev) {
      if (ev.key === 'Backspace') {
        this.details.tags.splice(-1, 1);
        this.$emit('update:releaseInfoChanged', true);
      }
    },

    // Account methods
    setAccount(fieldType) {
      // Clear account error when user attempts to select an account
      this.accountError = false;
      
      if (fieldType === 'artist' && !this.releaseStructure) {
        // If trying to select artist without structure, select structure first
        this.openStructureSelect();
        return;
      }

      // Only clear everything when clicking on the general account field
      if (fieldType === 'account') {
        this.details.artist.id = '';
        this.details.artist.name = '';
        this.details.label.id = '';
        this.details.label.title = '';
        this.$store.commit('SET_PRIMARY_ARTIST', null);
        this.$store.commit('SET_SELECTED_LABEL', null);
        this.$store.commit('SET_VARIOUS_ARTISTS', false);
        this.$emit('update:artist-id', null);
        this.$emit('update:releaseInfoChanged', true);
      }

      // Directly open the appropriate dialog based on which field was clicked
      if (fieldType === 'artist') {
        // Set the account type and directly open the artist selection dialog
        this.viewListAccounts = fieldType;
        this.$emit('open-account-modal', 'Artist');
      } else if (fieldType === 'label') {
        // Set the account type and directly open the label selection dialog
        this.viewListAccounts = 'Label';
        this.$emit('open-account-modal', 'Label');
      } else {
        // For any other case, just open the dialog with the current view type
        this.$emit('open-account-modal', this.viewListAccounts);
      }
    },
    
    openSecondaryArtistsSelector() {
      // Check if structure is selected
      if (!this.releaseStructure) {
        this.openStructureSelect();
        return;
      }
      
      // Open the secondary artists selector dialog
      this.showSecondaryArtistsDialog = true;
    },
    
    /**
     * Update the secondary artists display based on the provided artists array
     * This is used both by the watcher and direct selection methods
     */
    updateSecondaryArtistsDisplay(artists) {
      console.log('ReleaseInfo - updateSecondaryArtistsDisplay called with:', artists);
      
      // Update the secondary artists array in the form
      this.details.secondaryArtists = [...artists];
      console.log('ReleaseInfo - Updated details.secondaryArtists:', this.details.secondaryArtists);
      
      // Update the display text
      if (this.details.secondaryArtists.length > 0) {
        if (this.details.secondaryArtists.length === 1) {
          this.secondaryArtistsDisplay = this.details.secondaryArtists[0].name;
        } else {
          this.secondaryArtistsDisplay = `${this.details.secondaryArtists.length} artists selected`;
        }
        console.log('ReleaseInfo - Updated secondaryArtistsDisplay:', this.secondaryArtistsDisplay);
      } else {
        this.secondaryArtistsDisplay = '';
        console.log('ReleaseInfo - Cleared secondaryArtistsDisplay');
      }
      
      // Force Vue to re-render the input field
      this.$nextTick(() => {
        console.log('ReleaseInfo - Forcing re-render of secondaryArtistsDisplay');
        // Create a temporary variable and reassign to force reactivity
        const temp = this.secondaryArtistsDisplay;
        this.secondaryArtistsDisplay = '';
        this.$nextTick(() => {
          this.secondaryArtistsDisplay = temp;
        });
      });
      
      // Notify parent that data has changed
      this.$emit('update:releaseInfoChanged', true);
    },
    
    onSecondaryArtistsSelected(selectedArtists) {
      // Update the Vuex store to synchronize with the wizard
      this.SET_SECONDARY_ARTISTS([...selectedArtists]);
      
      // Update the display using our common method
      this.updateSecondaryArtistsDisplay(selectedArtists);
    },

    confirmChangeLabel() {
      this.viewListAccounts = 'Label';
      this.$emit('open-account-modal', this.viewListAccounts);
    },

    confirmChangeAccount() {
      this.viewListAccounts = 'Artist';
      this.$store.commit('SET_PRIMARY_ARTIST', null);
      this.$emit('open-account-modal', this.viewListAccounts);
    },

    clearInput(el) {
      if (el === 'label') {
        this.details.label.id = '';
        this.details.label.title = '';
        this.$store.commit('SET_SELECTED_LABEL', null);
        this.$emit('label-cleared');  // Emit event to parent component
        this.$emit('update:releaseInfoChanged', true);
        this.labelExplicitlyCleared = true; // Set flag when label is explicitly cleared by user
      }
      if (el === 'artist') {
        this.details.artist.id = '';
        this.details.artist.name = '';
        this.$store.commit('SET_PRIMARY_ARTIST', null);
        
        // Only reset VARIOUS_ARTISTS if we're in Various Artists Release mode
        if (this.releaseStructure === 'Various Artists Release') {
          this.$store.commit('SET_VARIOUS_ARTISTS', false);
        }
        
        // Important: We no longer clear the structure or secondary artists when clearing the primary artist
        // This allows the user to maintain their structure and secondary artists selection
        // even if they want to change the primary artist
        console.log('Primary artist cleared, but maintaining structure:', this.releaseStructure);
        console.log('Secondary artists maintained:', this.details.secondaryArtists.length);
        
        this.$emit('update:artist-id', null);
        this.$emit('update:releaseInfoChanged', true);
      }
      if (el === 'secondaryArtists') {
        this.details.secondaryArtists = [];
        this.secondaryArtistsDisplay = '';
        this.$emit('update:releaseInfoChanged', true);
      }
    },

    openStructureSelect() {
      console.log('openStructureSelect called');
      this.$emit('open-structure-select');
    },

    clearStructure() {
      // Clear the structure value
      this.releaseStructure = null;
      
      // Clear the structure in the Vuex store
      this.$store.commit('SET_RELEASE_STRUCTURE', null);
      
      // Reset the various artists flag
      this.$store.commit('SET_VARIOUS_ARTISTS', false);
      console.log('Structure cleared in both local state and Vuex store');

      // Reset artist if it was set
      if (this.details.artist.id) {
        this.details.artist.id = '';
        this.details.artist.name = '';
        this.$store.commit('SET_PRIMARY_ARTIST', null);
        this.$emit('update:artist-id', null);
      }
      
      // Reset secondary artists if any were set
      if (this.details.secondaryArtists.length > 0) {
        this.details.secondaryArtists = [];
        this.secondaryArtistsDisplay = '';
      }

      // Always reset the label error flag
      this.labelError = false;

      // Notify parent component that structure has been cleared
      this.$emit('structure-cleared');
      
      // Mark form as changed
      this.$emit('update:releaseInfoChanged', true);
      
      // Save changes to API if we have an existing release
      if (this.id) {
        // Only save if the form is valid
        if (!this.$v.details.title.$error) {
          console.log('Saving cleared structure to API...');
          
          // Create minimal album details to update
          const albumDetails = {
            id: +this.id,
            variousArtists: false,  // Clear the variousArtists flag
            artist: null,           // Clear the artist
            secondaryArtists: []    // Clear secondary artists
          };
          
          // Save to API
          this.EDIT_MUSIC_RELEASE(albumDetails)
            .then(() => {
              console.log('Successfully saved cleared structure to API');
            })
            .catch(error => {
              console.error('Error saving cleared structure to API:', error);
            });
        } else {
          console.log('Form has validation errors, not saving cleared structure to API');
        }
      } else {
        console.log('No release ID, not saving cleared structure to API');
      }
    },

    initGuidanceLines() {
      this.guidanceLines = [
        // Initialize all lines as visible and dimmed by default
        // They will be drawn as grey lines until the wizard is opened
        { id: 'labelLine', refName: 'labelInputDiv', visible: true, d: '', isFilled: false, isDimmed: true },
        { id: 'structureLine', refName: 'structureInputDiv', visible: false, d: '', isFilled: false, isDimmed: true }, // Will likely remain hidden
        { id: 'artistLine', refName: 'artistInputDiv', visible: true, d: '', isFilled: false, isDimmed: true },
        { id: 'secondaryArtistsLine', refName: 'secondaryArtistsInputDiv', visible: true, d: '', isFilled: false, isDimmed: true },
      ];
    },
    updateGuidanceLines() {
      if (!this.$refs.linesSvg || !this.$refs.wizardButton) {
        // Essential refs not available
        return;
      }

      const svgEl = this.$refs.linesSvg;
      // Ensure SVG has dimensions before attempting to get its BoundingClientRect
      // This can happen if it's initially display:none or similar.
      if (svgEl.offsetWidth === 0 && svgEl.offsetHeight === 0) {
        // console.warn('SVG for guidance lines has no dimensions yet.');
        return;
      }
      const svgRect = svgEl.getBoundingClientRect();

      const buttonEl = this.$refs.wizardButton;
      const buttonRect = buttonEl.getBoundingClientRect();

      if (buttonRect.width === 0 && buttonRect.height === 0) {
        // console.warn('Wizard button for guidance lines has no dimensions yet.');
        this.guidanceLines.forEach(line => line.visible = false); // Hide all lines
        return;
      }
      
      const targetPointX = buttonRect.left + buttonRect.width / 2 - svgRect.left;
      const targetPointY = buttonRect.top - svgRect.top; // Connect to top-center of button

      this.guidanceLines.forEach(line => {
        const inputEl = this.$refs[line.refName];
        // Check if inputEl exists and is visible (offsetParent is a common check for visibility)
        if (inputEl && inputEl.offsetParent !== null) {
          const inputRect = inputEl.getBoundingClientRect();
          if (inputRect.width === 0 && inputRect.height === 0) {
            // Element might be in DOM but not rendered with size
            line.visible = false;
            line.d = '';
            line.isFilled = false;
            line.isDimmed = false;
            return;
          }
          
          const startPointX = inputRect.left + inputRect.width / 2 - svgRect.left;
          const startPointY = inputRect.bottom - svgRect.top; // Connect from bottom-center of input

          const yDist = targetPointY - startPointY;
          
          // Determine the length of the initial vertical segment.
          // Aim for a noticeable segment to allow rounding to be visible.
          let verticalJog = 0;
          const minJogLength = 15; // Minimum length for the first vertical segment for visual rounding
          const maxJogLength = 30; // Maximum length for the first vertical segment

          if (Math.abs(yDist) > 1) { // Only create a distinct vertical jog if there's some Y travel
            if (Math.abs(yDist) < minJogLength * 2) {
              // If total Y distance is small, jog halfway
              verticalJog = yDist / 2;
            } else {
              // Otherwise, jog by minJogLength (or more, up to maxJogLength or 1/3 of yDist)
              verticalJog = Math.sign(yDist) * Math.min(Math.max(minJogLength, Math.abs(yDist) / 3), maxJogLength);
            }
          } else {
            // If yDist is very small or zero (inputs and button are almost horizontally aligned)
            // Force a small downward jog to maintain the V-H-V shape if possible
            verticalJog = minJogLength; 
          }

          const intermediateY = startPointY + verticalJog;

          // Path: M (start) -> V (intermediateY) -> H (targetX) -> V (targetY)
          line.d = `M ${startPointX},${startPointY} V ${intermediateY} H ${targetPointX} V ${targetPointY}`;
          line.visible = true;

          // Determine if the corresponding input is filled
          switch (line.id) {
            case 'labelLine':
              // Label line is filled if there's a label selected in our store
              line.isFilled = !!(this.SELECTED_LABEL && this.SELECTED_LABEL.id);
              break;
            case 'artistLine':
              // Artist line is filled if there's a primary artist selected in our store
              line.isFilled = !!(this.PRIMARY_ARTIST && this.PRIMARY_ARTIST.id);
              break;
            case 'secondaryArtistsLine':
              // Secondary artists line is filled if there are secondary artists in the store
              line.isFilled = !!(this.secondaryArtistsFromStore && Array.isArray(this.secondaryArtistsFromStore) && this.secondaryArtistsFromStore.length > 0);
              break;
            default:
              line.isFilled = false;
          }

          // Determine if the line should be dimmed based on release structure
          line.isDimmed = false; // Default to not dimmed

          const activeFlow = this.previewedFlowType;
          const activeStructure = this.previewedStructure !== null ? this.previewedStructure : this.releaseStructure;

          if (activeFlow) {
            // Step 1 Preview: Flow Type is known - this choice happens before structure selection
            if (activeFlow === 'artistFlow') {
              // In Artist Account flow, immediately dim the label line
              if (line.id === 'labelLine') {
                line.isDimmed = true;
              } else {
                // Keep artist and secondary artist lines active in artistFlow
                line.isDimmed = false;
              }
            } else if (activeFlow === 'labelFlow') {
              // In Record Label flow, initially keep ALL lines active (blue with animation)
              line.isDimmed = false;
              
              // Only apply structure-specific dimming if a structure is explicitly selected
              if (activeStructure) {
                if (activeStructure === 'Various Artists Release') {
                  // In Various Artists Release with Record Label flow:
                  // - Keep label line active (blue) - REQUIRED for Various Artists
                  // - Dim artist line but keep it visible
                  // - Dim secondary artist line but keep it visible
                  if (line.id === 'artistLine' || line.id === 'secondaryArtistsLine') {
                    line.isDimmed = true;
                    line.visible = true; // Explicitly keep them visible
                  } else if (line.id === 'labelLine') {
                    // Explicitly keep label line active
                    line.isDimmed = false;
                    line.visible = true; // Explicitly keep it visible
                  }
                } else if (activeStructure === 'Single Artist Release') {
                  // In Single Artist Release with Record Label flow:
                  // - Keep label line active (blue)
                  // - Keep artist line active (blue)
                  // - Only dim secondary artists line
                  if (line.id === 'secondaryArtistsLine') {
                    line.isDimmed = true;
                  } else {
                    // Explicitly keep label and artist lines active
                    line.isDimmed = false;
                  }
                }
                // For Multi-Artist Release, keep all lines active
              }
            }
          } else if (typeof activeStructure === 'string' && activeStructure) {
            // No Flow Preview, but Structure (previewed or confirmed) is known
            // Check if we have a label selected to determine if we're likely in label flow
            const hasLabelSelected = !!(this.details && this.details.label && this.details.label.id);
            // Check if we were recently in label flow (either by explicit selection or having a label)
            const wasInLabelFlow = this.lastFlowType === 'labelFlow' || hasLabelSelected;
            
            switch (activeStructure) {
              case 'Single Artist Release':
                // For Single Artist in Record Label flow (or if label is selected),
                // keep BOTH label and artist lines active
                if (line.id === 'labelLine') {
                  // Label line is active if we were in labelFlow or have a label selected
                  line.isDimmed = !wasInLabelFlow;
                } else if (line.id === 'secondaryArtistsLine') {
                  // Always dim secondary artists for Single Artist
                  line.isDimmed = true;
                } else if (line.id === 'artistLine') {
                  // Always keep artist active for Single Artist
                  line.isDimmed = false;
                }
                break;
              case 'Multi-Artist Release':
                // For Multi-Artist, keep label line active if we were in label flow,
                // otherwise dim it (likely in artist flow)
                if (line.id === 'labelLine') {
                  line.isDimmed = !wasInLabelFlow;
                }
                // Secondary artists and artist lines always active for Multi-Artist
                if (line.id === 'artistLine' || line.id === 'secondaryArtistsLine') {
                  line.isDimmed = false;
                }
                break;
              case 'Various Artists Release':
                // For Various Artists, primary and secondary artist lines are dimmed but still visible
                if (line.id === 'artistLine' || line.id === 'secondaryArtistsLine') {
                  line.isDimmed = true;
                  line.visible = true; // Explicitly keep them visible
                }
                // Label line is always active for Various Artists
                if (line.id === 'labelLine') {
                  line.isDimmed = false;
                  line.visible = true; // Explicitly keep it visible
                }
                break;
              default:
                line.isDimmed = false; // Should not happen if structure is valid
            }
          } else {
            // No previews, no confirmed structure - default to all lines active (not dimmed)
            // Or, base on whether they have existing data, e.g., if details.label.id, don't dim labelLine.
            // For simplicity now, keep them not dimmed if no other info.
            line.isDimmed = false; 
          }
        } else {
          line.visible = false;
          line.d = '';
          line.isFilled = false;
          line.isDimmed = false;
        }
      });
    },

    // Save method
    saveReleaseInfo(save) {
      // Reset errors
      this.accountError = false;
      this.labelError = false;
      
      // Ensure component state reflects store state for label
      if (this.SELECTED_LABEL && !this.labelExplicitlyCleared) {
        this.details.label.id = this.SELECTED_LABEL.id;
        this.details.label.title = this.SELECTED_LABEL.title;
      }
      
      // Format secondary artists for API
      if (this.details.secondaryArtists && this.details.secondaryArtists.length > 0) {
        // Ensure we have the proper format for the API
        this.details.secondaryArtists = this.details.secondaryArtists.map(artist => ({
          id: artist.id,
          name: artist.name,
          avatar: artist.avatar || null
        }));
      }
      
      if (save === 'continue') {
        this.$v.details.title.$touch();
        this.$v.details.genre.id.$touch();
        this.$v.details.subgenre.id.$touch();
        this.$v.datePicker.$touch();
        this.$v.releaseStructure.$touch();
        
        // Validate artist name for both structures
        if (this.releaseStructure === 'Single Artist Release' || this.releaseStructure === 'Multi-Artist Release') {
          this.$v.details.artist.name.$touch();
        }
        
        // Check if we're in Various Artists Release mode without a label
        if (this.releaseStructure === 'Various Artists Release' && !this.details.label.id) {
          this.labelError = true;
          return; // Stop further processing
        }
        
        // Check if any account is selected when in Single Artist Release mode
        if (this.releaseStructure === 'Single Artist Release' && !this.details.artist.id && !this.details.label.id) {
          this.accountError = true;
          return; // Stop further processing
        }
        
        // Structure validation is handled by Vuelidate
        if (!this.releaseStructure) {
          return;
        }
      }
      
      this.$v.details.title.$touch();

      // Modified validation condition for continue action
      // If in Various Artists mode, don't check artist.name validation
      // For Various Artists Release, we need a label but not an artist
      const artistNameValid = (this.releaseStructure === 'Various Artists Release') || this.VARIOUS_ARTISTS || !this.$v.details.artist.name.$error;
      
      if ((save === 'continue' &&
          this.$v.details.title.$error === false &&
          this.$v.details.genre.id.$error === false &&
          this.$v.details.subgenre.id.$error === false &&
          this.$v.datePicker.$error === false &&
          this.$v.releaseStructure.$error === false &&
          artistNameValid) ||
          (save === 'save' && this.$v.details.title.$error === false) ||
          save === 'publish' && this.$v.details.title.$error === false) {

        // For Various Artists mode, skip artist subscription check
        let artistSubscriptionValid = this.releaseStructure === 'Various Artists Release' || this.VARIOUS_ARTISTS;
        
        if (!artistSubscriptionValid) {
          let artistID = this.details.artist.id;
          let selectedArtist = this.YOUR_ARTISTS.find(el => el.id === artistID);

          if (selectedArtist) {
            artistSubscriptionValid = selectedArtist.hasActiveSubscription || 
              (selectedArtist.subscription && selectedArtist.subscription.canActivate);
          } else {
            // No artist selected, but in single artist mode
            artistSubscriptionValid = false;
          }
          
          if (!artistSubscriptionValid) {
            this.artistSubscriptionError = true;
            return;
          }
        }

        if (!this.releaseInfoChanged) {
          this.$emit('update:createLoading', true);
          this.$emit('change-step', 2);
          return;
        }

        // Ensure record label is explicitly null when not set
        const recordLabel = !this.details.label.id ? null : `/record_labels/${this.details.label.id}`;
        
        // Debug: Log recordLabel value that will be saved
        console.log('saveReleaseInfo - saving recordLabel value:', recordLabel);

        const albumDetails = {
          title: this.details.title.normalize('NFC'),
          recordLabel: recordLabel,
          description: !this.details.description ? null : this.details.description.normalize('NFC'),
          type: 'album',
          tags: !this.details.tags ? null : this.details.tags,
          genre: !this.details.genre.id ? null : `genres/${this.details.genre.id}`,
          subGenres: !this.details.subgenre.id ? [] : [`sub_genres/${this.details.subgenre.id}`],
          releasedAt: new Date(this.datePicker).toISOString(),
          // Set variousArtists flag based on the selected structure
          variousArtists: this.releaseStructure === 'Various Artists Release',
        };
        
        // Start counting relations
        let relationCount = 0;
        
        // Log each relation for debugging
        console.log('--- MUSIC RELEASE RELATIONS COUNT ---');
        
        // Count recordLabel relation if present
        if (recordLabel) {
          relationCount++;
          console.log(`Record Label: ${recordLabel} (count: ${relationCount})`);
        }
        
        // Count genre relation if present
        if (this.details.genre.id) {
          relationCount++;
          console.log(`Genre: genres/${this.details.genre.id} (count: ${relationCount})`);
        }
        
        // Count subgenre relation if present
        if (this.details.subgenre.id) {
          relationCount++;
          console.log(`SubGenre: sub_genres/${this.details.subgenre.id} (count: ${relationCount})`);
        }
        
        // Also update the store to match
        this.$store.commit('SET_VARIOUS_ARTISTS', this.releaseStructure === 'Various Artists Release');
        
        // Set artist for all structures using IRI format
        if (this.details.artist.id) {
          albumDetails.artist = `/artists/${this.details.artist.id}`;
          relationCount++;
          console.log(`Artist: /artists/${this.details.artist.id} (count: ${relationCount})`);
        } else {
          albumDetails.artist = null;
        }
        
        // For Multi-Artist Release structure
        if (this.releaseStructure === 'Multi-Artist Release') {
          // Add secondary artists if any are selected using IRI format
          if (this.details.secondaryArtists && this.details.secondaryArtists.length > 0) {
            albumDetails.secondaryArtists = this.details.secondaryArtists.map(artist => {
              relationCount++;
              console.log(`Secondary Artist: /artists/${artist.id} (count: ${relationCount})`);
              return `/artists/${artist.id}`;
            });
            console.log(`Total Secondary Artists: ${this.details.secondaryArtists.length}`);
          } else {
            // Explicitly set empty array to avoid undefined
            albumDetails.secondaryArtists = [];
          }
        }

        this.$emit('update:createLoading', true);

        // Log total relations count before submission
        console.log(`TOTAL RELATIONS COUNT: ${relationCount}`);
        console.log('Full album details payload:', JSON.stringify(albumDetails, null, 2));
        console.log('--- END MUSIC RELEASE RELATIONS COUNT ---');
        
        if (!this.id) {
          this.ADD_MUSIC_RELEASE(albumDetails)
            .then((response) => {
              console.log('Music release creation response:', response);
              // Log any errors if present
              if (response && response.error) {
                console.error('Music release creation error:', response.error);
              }
              if (this.TEMP_CREATED_ALBUMS_ID) {
                this.$emit('navigate-to-created', this.TEMP_CREATED_ALBUMS_ID);
                this.$emit('change-step', 2);
                this.$emit('update:releaseInfoChanged', false);
              }
            })
            .finally(() => {
              this.$emit('update:createLoading', false);
            });
        } else {
          albumDetails.id = +this.id;
          this.EDIT_MUSIC_RELEASE(albumDetails)
            .then((response) => {
              console.log('Music release update response:', response);
              // Log any errors if present
              if (response && response.error) {
                console.error('Music release update error:', response.error);
              }
              if (this.TEMP_CREATED_ALBUMS_ID) {
                this.$emit('change-step', 2);
                this.$emit('update:releaseInfoChanged', false);
              }
            })
            .finally(() => {
              this.$emit('update:createLoading', false);
            });
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* Account Selection Section Styling */
.account-selection-section {
  background: rgba(24, 32, 52, 0.4);
  border-radius: 12px;
  border: 1px solid rgba(32, 196, 245, 0.2);
  padding: 20px;
  margin: 20px 0;
  position: relative;
  overflow: hidden;
  
  // Subtle pulsing glow effect
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 12px;
    z-index: -1;
    animation: account-section-glow 3s ease-in-out infinite;
  }
  
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    
    h3 {
      font-size: 18px;
      color: #20C4F5;
      margin: 0;
      font-weight: 500;
    }
    
    .selection-guidance {
      display: flex;
      align-items: center;
      font-size: 13px;
      color: rgba(255, 255, 255, 0.7);
      
      .wizard-arrow {
        margin-left: 10px;
        width: 22px;
        height: 22px;
        background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2320C4F5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cline x1='7' y1='17' x2='17' y2='7'%3E%3C/line%3E%3Cpolyline points='7 7 17 7 17 17'%3E%3C/polyline%3E%3C/svg%3E") center/contain no-repeat;
        transform: rotate(90deg);
        opacity: 0.8;
        animation: bounce-arrow 2s infinite;
      }
    }
  }
  
  // Make account selection fields occupy a single line with standard spacing
  .row_fields.single-line-fields {
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    width: 100%;
    gap: 0; // Remove gap property to use standard margin approach
    
    .dropdown_style {
      flex: 1;
      min-width: 0; // Allow flex items to shrink below content size
      margin-bottom: 15px;
      
      // Apply standard margin-right of 20px to match other form elements
      &:not(:last-child) {
        margin-right: 20px;
      }
      
      // Hide elements with the hidden class
      &.hidden {
        display: none;
      }
      
      // Input state styles for dropdown_style based on guidance line states
      &.input-active {
        .dropdown_inside {
          border: 1px solid #20C4F5;
          box-shadow: 0 0 8px rgba(32, 196, 245, 0.3);
          
          div {
            background: rgba(32, 196, 245, 0.1);
          }
        }
      }
      
      &.input-filled {
        .dropdown_inside {
          border: 1px solid #4CAF50;
          box-shadow: 0 0 8px rgba(76, 175, 80, 0.3);
          
          div {
            background: rgba(76, 175, 80, 0.1);
            color: rgba(255, 255, 255, 0.9);
          }
          
          label {
            color: rgba(76, 175, 80, 0.9);
          }
        }
      }
      
      &.input-dimmed {
        .dropdown_inside {
          border: 1px solid rgba(130, 132, 145, 0.4);
          
          div {
            color: rgba(130, 132, 145, 0.6);
          }
          
          label {
            color: rgba(130, 132, 145, 0.6);
          }
        }
      }
      
      @media (max-width: 992px) {
        flex-basis: 100%;
        margin-right: 0; // Remove margin on mobile when stacked
      }
    }
    
    @media (max-width: 992px) {
      flex-wrap: wrap;
    }
  }
  
  // Full width account selection section
  &.full_width {
    width: 100%;
  }
  
  // Ensure dropdown_disable styling exactly matches genre input
  .dropdown_style.dropdown_disable {
    .dropdown_inside {
      height: auto;
      width: 100%;
      position: relative;
      
      div {
        border-radius: 10px;
        cursor: default;
        border: 1px solid transparent; // Default transparent border for clean state
        color: rgba(130, 132, 145, 0.8);
        background: rgba(227, 227, 227, 0.12);
        padding: 30px 15px 15px; // Increased top padding to move text down
        font-size: 14px;
        width: 100%;
        min-height: 63px; // Match standard input height
        display: flex;
        align-items: flex-start; // Align text to start
        transition: all 0.3s ease; // Add transition for color changes
      }
      
      label {
        cursor: default;
        color: rgba(255, 255, 255, 0.6);
        position: absolute;
        top: 10px; // Position label at top
        left: 15px;
        font-size: 14px;
        transition: all 0.2s ease;
        z-index: 1;
      }
    }
    
    .v-menu {
      display: none;
    }
  }
}

/* Account Wizard Button */
.wizard-button-container {
  display: flex;
  justify-content: center;
  margin-top: 35px; // Increased margin to move button lower by 20px
  padding-top: 0; // Reset padding if it was to make space, SVG is overlay
}

.account-wizard-btn {
  background: rgba(32, 196, 245, 0.15);
  color: #20C4F5;
  border: 1px solid rgba(32, 196, 245, 0.4);
  border-radius: 8px;
  padding: 12px 22px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 0 8px rgba(32, 196, 245, 0.2);
  position: relative;
  overflow: hidden;
  z-index: 2;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(32, 196, 245, 0.1), rgba(32, 196, 245, 0), rgba(32, 196, 245, 0.1));
    animation: btn-shine 3s ease-in-out infinite;
  }
  
  &:hover {
    background: rgba(32, 196, 245, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(32, 196, 245, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &.completed {
    background: rgba(76, 175, 80, 0.25);
    color: #4CAF50;
    border-color: rgba(76, 175, 80, 0.6);
    box-shadow: 0 0 8px rgba(76, 175, 80, 0.3);
    
    &:before {
      background: linear-gradient(45deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0), rgba(76, 175, 80, 0.1));
      animation: btn-shine-green 3s ease-in-out infinite;
    }
    
    &:hover {
      background: rgba(76, 175, 80, 0.35);
      box-shadow: 0 5px 15px rgba(76, 175, 80, 0.4);
    }
  }
}

.loader-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  width: 100%;
  background-color: rgba(18, 18, 35, 0.03);
  border-radius: 8px;
  backdrop-filter: blur(2px);
  
  .loader-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 30px;
    background-color: rgba(18, 18, 35, 0.7);
    border-radius: 12px;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    max-width: 80%;
    
    .v-progress-circular {
      margin-bottom: 20px;
      filter: drop-shadow(0 0 8px rgba(32, 196, 245, 0.5));
    }
    
    .loader-text {
      text-align: center;
      
      h3 {
        color: #fff;
        font-weight: 500;
        margin-bottom: 8px;
        font-size: 18px;
      }
      
      p {
        color: rgba(255, 255, 255, 0.7);
        font-size: 14px;
        margin: 0;
      }
    }
  }
}

/* Guidance Lines Styling */
.guidance-lines-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none; /* Allows clicks to pass through */
  z-index: 1; /* Below the button, above other static content if necessary */
  
  &.hidden {
    display: none;
  }
}

.animated-guidance-line {
  stroke: #20C4F5;
  stroke-width: 1px;
  stroke-linecap: round;
  stroke-linejoin: round; /* Added for rounded bends */
  fill: none; /* Important for paths that are only strokes */
  transition: stroke 0.3s ease; /* Smooth color transition */

  &.filled {
    stroke: #4CAF50; /* Green color for filled state */
  }

  &.dimmed {
    stroke: #888888; /* Gray color for dimmed state */
  }
}

.energy-pip {
  fill: #20C4F5; /* Pip color - can be brighter or different */
  stroke: none;
  /* Potentially add a subtle glow or shadow if desired */
  /* filter: drop-shadow(0 0 1px #20C4F5); */
  transition: fill 0.3s ease; /* Smooth color transition */

  &.filled {
    fill: #4CAF50; /* Green color for filled state */
  }

  &.dimmed {
    fill: #888888; /* Gray color for dimmed state */
  }
}

</style>

<style lang="scss">
/* Animations for the Account Selection Section */
@keyframes account-section-glow {
  0% { box-shadow: 0 0 5px rgba(32, 196, 245, 0.2); }
  50% { box-shadow: 0 0 15px rgba(32, 196, 245, 0.3); }
  100% { box-shadow: 0 0 5px rgba(32, 196, 245, 0.2); }
}

@keyframes bounce-arrow {
  0%, 100% { transform: translateY(0) rotate(90deg); }
  50% { transform: translateY(-5px) rotate(90deg); }
}

@keyframes tooltip-fade {
  0% { opacity: 0; transform: translateX(-50%) translateY(10px); }
  100% { opacity: 1; transform: translateX(-50%) translateY(0); }
}

@keyframes btn-shine {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

@keyframes btn-shine-green {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* The flow-line-animation keyframes are no longer needed as the pips provide the flow effect.
   Removing the commented out block to resolve linter errors. */

</style>
